/**
 * Bill Validation — a bill's customer report: what it says, and whether it went.
 *
 *   report exists → sent
 *                 ↘ send failed → (retry) → sent
 *
 * A report EXISTS when the bill has an expected invoice and a retailer invoice
 * to compare it with — that is everything BillReportScreen needs. It is not a
 * stored flag: `report_data.generated_at` is only a timestamp the bill run
 * writes, and bills priced from the bill page never get one.
 *
 * What the report says — overcharge, undercharge or within range — is the
 * report's own verdict (`classifyVariance` on the totals after exclusions), so
 * the Bulk Customer Report list can never disagree with the PDF it sends.
 *
 * Send status is DERIVED on every read, never stored. It comes from
 * bv_report_send: the latest attempt decides sent vs send failed, and each send
 * records the totals it stated (migration 125), so a re-run or an invoice
 * correction after sending reads "Changed since sent".
 *
 * Dependency-free apart from the pure bill helpers — the nightly job imports
 * `mergeReportStamp` from here.
 */

import type { ActualBillResult, BvBill, BvReportData, ShadowBillResult } from '../types/billTypes';
import { effectiveInvoiceResult } from './effectiveInvoice';
import { classifyVariance } from './reportFormulas';
import { billVerdict, type BillVerdict } from './billVerdict';
import {
  applyExclusions, applyExclusionsToActual, effectiveExclusions, isSectionExcluded,
} from '../utils/calcExclusions';

// ── What the report says ────────────────────────────────────────────────────

export type ReportFigures = {
  shadowResult: ShadowBillResult;
  actualResult: ActualBillResult;
  gstExcluded: boolean;
};

/**
 * Both sides of the report with the bill's exclusions applied — the figures
 * BillReportScreen must be given, the same way Bill Run Detail and the server's
 * report context compute them. Null when the bill has no report.
 */
export function reportFigures(bill: Partial<BvBill>): ReportFigures | null {
  const sr = bill.shadow_result as ShadowBillResult | null | undefined;
  const ar = effectiveInvoiceResult(bill) as ActualBillResult | null;
  if (!sr || !ar) return null;
  const exclusions = effectiveExclusions(bill);
  return {
    shadowResult: applyExclusions(sr, exclusions),
    actualResult: applyExclusionsToActual(ar, exclusions),
    gstExcluded: isSectionExcluded('gst', exclusions),
  };
}

export type ReportBucket = 'overcharge' | 'undercharge' | 'within_range';

export const BUCKET_LABELS: Record<ReportBucket, string> = {
  overcharge: 'Overcharge',
  undercharge: 'Undercharge',
  within_range: 'Within range',
};

/**
 * The report's verdict in three buckets. Matched, within limits and acceptable
 * (accuracy ≥ 95%, or ≤ $0.50 apart) are all within range.
 *
 * Pass the bill's billVerdict: it judges only the charges we validate, the rest
 * added back at the bill's amounts. The figures alone still carry unmapped lines
 * and the GST on them on the invoice side, so they are the fallback only.
 */
export function reportBucket(figures: ReportFigures, verdict?: BillVerdict | null): ReportBucket {
  if (verdict) return verdict.bucket;
  const kind = classifyVariance(figures.shadowResult.total, figures.actualResult.total);
  if (kind === 'over') return 'overcharge';
  if (kind === 'under') return 'undercharge';
  return 'within_range';
}

// ── Whether it went ─────────────────────────────────────────────────────────

export type ReportSendStatus =
  | 'no_report'
  | 'not_sent'
  | 'send_failed'
  | 'sent'
  | 'changed_since_sent';

export const SEND_STATUS_LABELS: Record<ReportSendStatus, string> = {
  no_report: 'No report',
  not_sent: 'Not sent',
  send_failed: 'Send failed',
  sent: 'Sent',
  changed_since_sent: 'Changed since sent',
};

/** One bv_report_send row that has finished: sent or failed. */
export type SendAttempt = {
  status: 'sent' | 'failed';
  /** created_at — server time. */
  at: string;
  error: string | null;
};

/** The latest successful send and the totals its report stated. */
export type LastSent = {
  /** sent_at */
  at: string;
  /** Null for sends from before migration 125. */
  expectedTotal: number | null;
  invoiceTotal: number | null;
};

/** A bill's send history, as far as its status needs it. */
export type SendHistory = {
  lastSent: LastSent | null;
  /** Latest finished attempt of either outcome, or null. */
  lastAttempt: SendAttempt | null;
};

export const NO_SENDS: SendHistory = { lastSent: null, lastAttempt: null };

/** A bv_report_send row, as far as send history needs it. */
export type SendRowLike = {
  bill_id: string | null;
  status: 'queued' | 'sent' | 'failed' | string;
  sent_at: string | null;
  created_at: string;
  error: string | null;
  expected_total?: number | string | null;
  invoice_total?: number | string | null;
};

/** NUMERIC can arrive as a string; absent stays null. */
function toMoney(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * One history per bill from bv_report_send rows, in any order. `queued` rows are
 * in flight and change nothing until they settle — counting one as the latest
 * attempt would hide the failure or success before it.
 */
export function foldSendHistory(rows: SendRowLike[]): Record<string, SendHistory> {
  const out: Record<string, SendHistory> = {};
  const later = (a: string, b: string) => Date.parse(a) > Date.parse(b);
  for (const row of rows) {
    if (!row.bill_id || (row.status !== 'sent' && row.status !== 'failed')) continue;
    const h = (out[row.bill_id] ??= { lastSent: null, lastAttempt: null });
    if (row.status === 'sent' && row.sent_at && (!h.lastSent || later(row.sent_at, h.lastSent.at))) {
      h.lastSent = {
        at: row.sent_at,
        expectedTotal: toMoney(row.expected_total),
        invoiceTotal: toMoney(row.invoice_total),
      };
    }
    if (!h.lastAttempt || later(row.created_at, h.lastAttempt.at)) {
      h.lastAttempt = { status: row.status, at: row.created_at, error: row.error };
    }
  }
  return out;
}

/** Totals equal to the cent. Stored numerics and float maths disagree past that. */
function sameMoney(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100);
}

/** The report can be shown: there is an expected invoice and a retailer invoice. */
/**
 * The expected invoice has no network charges because they could not be
 * priced: no network line with an amount, and the calculation's own "No
 * network charges for this period" warning. That happens when the bill run
 * never ran (usage data does not cover the period) and the invoice parse
 * re-priced retail and metering from the invoice's kWh alone.
 *
 * Such a bill is INCOMPLETE, not an overcharge: NMI 41037726865 (Aug 2026)
 * showed expected $1,119.65 against $1,973.73 as a +$854.08 overcharge with
 * $646 of network charges simply missing.
 *
 * Derived from the result, not stored, so it clears by itself once NEM
 * analysis or a re-run adds the network lines.
 */
export function networkNotPriced(shadow: Partial<ShadowBillResult> | null | undefined): boolean {
  if (!shadow) return false;
  const priced = (shadow.lines ?? []).some((l) => l.section === 'distribution' && Number(l.amount) !== 0);
  const warned = (shadow.warnings ?? []).some((w) => /^no network charges\b/i.test(String(w)));
  return !priced && warned;
}

const isKwh = (unit: string | null | undefined) => /kwh/i.test(unit ?? '');
// Market and ancillary fees sit in the network section on some invoices but
// are not network energy; a bill without network energy can still carry them.
const MARKET_FEE = /aemo|market|ancillary|participant|ufe/i;

/**
 * Network demand priced, network energy not: the invoice bills network energy
 * (kWh) and the expected invoice has no network kWh line with an amount. The
 * bill was priced before the period's network usage was computed; demand still
 * prices from the lookback months, so networkNotPriced's warning never fires.
 *
 * Oct 2026: 278 bills (e.g. United Energy LVKVATOU1: Peak and Off Peak Energy
 * $741 invoiced, none expected) showed as overcharges. Sep 2026: 6, Aug: 0.
 */
export function networkEnergyNotPriced(bill: Partial<BvBill>): boolean {
  const shadow = bill.shadow_result as ShadowBillResult | null | undefined;
  const invoice = effectiveInvoiceResult(bill);
  if (!shadow || !invoice) return false;
  const network = (shadow.lines ?? []).filter((l) => l.section === 'distribution' && Number(l.amount) !== 0);
  if (network.length === 0 || network.some((l) => isKwh(l.usage_unit))) return false;
  return (invoice.lines ?? []).some((l) => l.section === 'distribution' && Number(l.amount) > 0
    && (isKwh(l.usage_unit) || isKwh(l.volume_unit)) && !MARKET_FEE.test(l.description ?? ''));
}

/** Network charges missing in whole (networkNotPriced) or in part (networkEnergyNotPriced). */
export function networkIncomplete(bill: Partial<BvBill>): boolean {
  return networkNotPriced(bill.shadow_result as ShadowBillResult) || networkEnergyNotPriced(bill);
}

export function hasReport(bill: Partial<BvBill>): boolean {
  // An incomplete calculation is not a report yet: its "variance" is the
  // charges we could not price.
  return !!bill.shadow_result && !!effectiveInvoiceResult(bill) && !networkIncomplete(bill);
}

/** `verdict` is the bill's billVerdict (with its charge map where the page has
 *  one): the totals a report states, and so what a send recorded. */
export function reportSendStatus(
  bill: Partial<BvBill>,
  history: SendHistory,
  verdict?: BillVerdict | null,
): ReportSendStatus {
  const figures = reportFigures(bill);
  if (!figures) return 'no_report';
  // Not a report to send until the network charges are priced (see networkIncomplete).
  if (networkIncomplete(bill)) return 'no_report';
  // A failed send leaves nothing new with the client — it is the outcome to act on.
  if (history.lastAttempt?.status === 'failed') return 'send_failed';

  const sent = history.lastSent;
  if (!sent) return 'not_sent';
  // A send from before the totals were recorded cannot be "changed since".
  if (sent.expectedTotal == null || sent.invoiceTotal == null) return 'sent';
  const v = verdict === undefined ? billVerdict(bill) : verdict;
  const unchanged =
    sameMoney(v?.compared.expected ?? figures.shadowResult.total, sent.expectedTotal) &&
    sameMoney(v?.compared.invoice ?? figures.actualResult.total, sent.invoiceTotal);
  return unchanged ? 'sent' : 'changed_since_sent';
}

/**
 * May this report go out in a bulk send? One never sent, or one whose last send
 * failed (a retry). A report already with the client — changed since or not —
 * is re-sent deliberately from its own Send to client, never in bulk.
 */
export function canSend(bill: Partial<BvBill>, history: SendHistory, verdict?: BillVerdict | null): boolean {
  const status = reportSendStatus(bill, history, verdict);
  return status === 'not_sent' || status === 'send_failed';
}

/**
 * What the bill run writes when it (re)prices a bill. Every existing field
 * survives — the adviser's notes in particular.
 */
export function mergeReportStamp(existing: BvReportData | null | undefined, nowIso: string): BvReportData {
  return { ...(existing ?? {}), generated_at: nowIso };
}
